/**
 * The account's motion engine (redesign §1, §35): the site's springs and press
 * feedback, copied here rather than imported, since the three web projects share
 * nothing but a copied palette (Codex 1040). The springs below are `site/src/
 * motion/springs.ts` as it stands; `bindPress` is `press.ts` with the account's
 * two additions (§1.2): `data-press="flat"` sinks without the hover rise, and an
 * element marked `aria-disabled="true"` does not react at all.
 */
/**
 * The site's one motion engine, from Claude Design's Apple v3 pages: springs
 * described by a damping ratio and a response (seconds per oscillation), which
 * are interruptible — a new target starts from the value on screen and keeps
 * its velocity — and short cubic tweens for what should not overshoot.
 *
 * Every page and the chrome share one instance, so one frame loop runs however
 * many things are moving, and it stops when nothing is. It only ever writes
 * `opacity` and `transform` (translate, scale, and a per-axis scale), so the
 * element's own style keeps everything else.
 *
 * A reader who asked for less motion gets the end state at once, except for
 * opacity, which takes a 200 ms fade — a change still reads as one. `force`
 * overrides that for press feedback, which is a response to their own finger.
 */

type Key = 'o' | 'x' | 'y' | 's' | 'sx' | 'sy';
export type Props = Partial<Record<Key, number>>;

export interface SpringOptions {
  /** Damping ratio: 1 settles without overshoot, under 1 bounces. */
  d?: number;
  /** Response, in seconds: the period of the undamped spring. */
  r?: number;
  /** Seconds before it starts. */
  delay?: number;
  /** A cubic ease-out tween of this many seconds instead of a spring. */
  tw?: number;
  /** Moves even under `prefers-reduced-motion`. */
  force?: boolean;
  /** Called once every key in this call has settled. */
  done?: () => void;
}

interface Channel {
  v: number;
  t: number;
  vel: number;
  /** 0 at rest, 1 spring, 2 tween. */
  m: 0 | 1 | 2;
  d: number;
  r: number;
  wait: number;
  f: number;
  t0: number;
  dur: number;
}

interface Body {
  el: HTMLElement;
  p: Partial<Record<Key, Channel>>;
  cb: { keys: Key[]; fn: () => void }[];
  o0: number;
}

/** A frame callback; it returns whether it still wants frames. */
export type Ticker = (dt: number) => boolean;

const bodies = new WeakMap<HTMLElement, Body>();
const live = new Set<Body>();
const tickers = new Set<Ticker>();
let raf = 0;
let timeout = 0;
let running = false;
let last = 0;
let media: MediaQueryList | null = null;
let travel: (Channel & { set: number }) | null = null;

/** Whether the reader asked for less motion, followed live. */
export function reduced(): boolean {
  if (!media) {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    media = window.matchMedia('(prefers-reduced-motion: reduce)');
  }
  return media.matches;
}

function body(el: HTMLElement): Body {
  let b = bodies.get(el);
  if (!b) {
    const o = parseFloat(el.style.opacity);
    b = { el, p: {}, cb: [], o0: Number.isNaN(o) ? 1 : o };
    bodies.set(el, b);
  }
  return b;
}

function channel(b: Body, k: Key): Channel {
  let c = b.p[k];
  if (!c) {
    const v = k === 'o' ? b.o0 : k === 's' || k === 'sx' || k === 'sy' ? 1 : 0;
    c = { v, t: v, vel: 0, m: 0, d: 1, r: 0.4, wait: 0, f: v, t0: 0, dur: 0 };
    b.p[k] = c;
  }
  return c;
}

function paint(b: Body): void {
  const { p, el } = b;
  if (p.o) el.style.opacity = p.o.v.toFixed(4);
  if (p.x || p.y || p.s || p.sx || p.sy) {
    el.style.transform =
      `translate3d(${(p.x ? p.x.v : 0).toFixed(2)}px,${(p.y ? p.y.v : 0).toFixed(2)}px,0) ` +
      `scale(${(p.s ? p.s.v : 1).toFixed(4)}) scale(${(p.sx ? p.sx.v : 1).toFixed(4)},${(p.sy ? p.sy.v : 1).toFixed(4)})`;
  }
}

/** Puts `el` at these values now, with no velocity. */
export function set(el: HTMLElement | null | undefined, props: Props): void {
  if (!el) return;
  const b = body(el);
  for (const k of Object.keys(props) as Key[]) {
    const c = channel(b, k);
    c.v = c.t = props[k]!;
    c.vel = 0;
    c.m = 0;
  }
  paint(b);
}

/** Moves `el` towards these values, from wherever it is on screen. */
export function to(el: HTMLElement | null | undefined, props: Props, options: SpringOptions = {}): void {
  if (!el) return;
  const b = body(el);
  const now = performance.now();
  const keys = Object.keys(props) as Key[];
  for (const k of keys) {
    const c = channel(b, k);
    c.t = props[k]!;
    if (reduced() && !options.force) {
      if (k === 'o') {
        c.m = 2;
        c.f = c.v;
        c.t0 = now;
        c.dur = 200;
      } else {
        c.v = c.t;
        c.vel = 0;
        c.m = 0;
      }
      continue;
    }
    if (options.tw) {
      c.m = 2;
      c.f = c.v;
      c.t0 = now + (options.delay ?? 0) * 1000;
      c.dur = options.tw * 1000;
    } else {
      c.m = 1;
      c.d = options.d ?? 1;
      c.r = options.r ?? 0.4;
      c.wait = now + (options.delay ?? 0) * 1000;
    }
  }
  if (options.done) b.cb.push({ keys, fn: options.done });
  paint(b);
  live.add(b);
  kick();
}

/** Whether `el` is moving under a spring or a tween right now. */
export function moving(el: HTMLElement): boolean {
  const b = bodies.get(el);
  return b !== undefined && live.has(b);
}

/** Runs `tick` on every frame until it returns false; returns its removal. */
export function addTicker(tick: Ticker): () => void {
  tickers.add(tick);
  kick();
  return () => tickers.delete(tick);
}

/** Wakes the loop for a ticker that has frames to ask for again. */
export function kick(): void {
  if (running) return;
  if (!raf && !timeout) last = performance.now();
  running = true;
  const run = (): void => {
    if (!running) return;
    window.clearTimeout(timeout);
    cancelAnimationFrame(raf);
    raf = 0;
    timeout = 0;
    frame(performance.now());
  };
  raf = requestAnimationFrame(run);
  // A tab in the background gets no animation frames; the timeout keeps a
  // spring from freezing half-way until it comes back.
  timeout = window.setTimeout(run, 40);
}

function step(c: Channel, dt: number): void {
  const w = (2 * Math.PI) / c.r;
  const n = Math.max(1, Math.ceil(dt / 0.004));
  const h = dt / n;
  for (let i = 0; i < n; i++) {
    const a = -w * w * (c.v - c.t) - 2 * c.d * w * c.vel;
    c.vel += a * h;
    c.v += c.vel * h;
  }
}

function frame(now: number): void {
  const dt = Math.min(1, Math.max(0, (now - last) / 1000));
  last = now;
  running = false;
  for (const b of live) {
    let busy = false;
    for (const k of Object.keys(b.p) as Key[]) {
      const c = b.p[k]!;
      if (c.m === 1) {
        if (now < c.wait) {
          busy = true;
          continue;
        }
        step(c, dt);
        const e = k === 'x' || k === 'y' ? 0.05 : 0.0006;
        if (Math.abs(c.v - c.t) < e && Math.abs(c.vel) < e * 12) {
          c.v = c.t;
          c.vel = 0;
          c.m = 0;
        } else busy = true;
      } else if (c.m === 2) {
        if (now < c.t0) {
          busy = true;
          continue;
        }
        const u = Math.min(1, (now - c.t0) / c.dur);
        const pv = c.v;
        c.v = c.f + (c.t - c.f) * (1 - Math.pow(1 - u, 3));
        c.vel = dt ? (c.v - pv) / dt : 0;
        if (u >= 1) {
          c.v = c.t;
          c.vel = 0;
          c.m = 0;
        } else busy = true;
      }
    }
    paint(b);
    if (b.cb.length) {
      b.cb = b.cb.filter((cb) => {
        if (cb.keys.every((k) => b.p[k]!.m === 0)) {
          cb.fn();
          return false;
        }
        return true;
      });
    }
    if (!busy && !b.cb.length) live.delete(b);
  }
  if (travel) {
    const T = travel;
    // The reader scrolled during the trip: they win.
    if (Math.abs(window.scrollY - T.set) > 3) travel = null;
    else {
      step(T, dt);
      window.scrollTo(0, T.v);
      T.set = window.scrollY;
      if (Math.abs(T.v - T.t) < 0.5 && Math.abs(T.vel) < 5) {
        window.scrollTo(0, T.t);
        travel = null;
      }
    }
  }
  let ticking = false;
  for (const tick of tickers) if (tick(dt)) ticking = true;
  if (live.size || travel || ticking) kick();
}

/** Scrolls the window to `y` on a spring, or at once under reduced motion. */
export function scrollToY(y: number): void {
  const top = Math.max(0, Math.min(y, document.documentElement.scrollHeight - window.innerHeight));
  if (reduced()) {
    travel = null;
    window.scrollTo(0, top);
    return;
  }
  travel = {
    v: window.scrollY,
    vel: travel ? travel.vel : 0,
    t: top,
    m: 1,
    d: 1,
    r: 0.6,
    wait: 0,
    f: 0,
    t0: 0,
    dur: 0,
    set: window.scrollY
  };
  kick();
}

/** Where a spring scroll is headed, or null when none is under way. */
export function scrollTarget(): number | null {
  return travel ? travel.t : null;
}

/** Drops a spring scroll in flight, as a wheel or a touch does. */
export function stopScroll(): void {
  travel = null;
}

/* ── press (§1.2) ──────────────────────────────────────────────────────────── */

/**
 * Anything marked `data-press` sinks a little on pointer-down and springs back
 * from wherever it is on release; under a mouse it rises a pixel on hover
 * (`lift`: a card, four pixels, sinking less; `flat`: no rise). One delegated
 * listener set on the root covers markup drawn later.
 */
export function bindPress(root: HTMLElement): () => void {
  let pressed: HTMLElement | null = null;
  const find = (event: Event): HTMLElement | null => {
    const el = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-press]') : null;
    return el && el.getAttribute('aria-disabled') !== 'true' && !(el as HTMLButtonElement).disabled ? el : null;
  };
  const inside = (el: HTMLElement, event: PointerEvent): boolean =>
    event.relatedTarget instanceof Node && el.contains(event.relatedTarget);
  const rise = (el: HTMLElement): number => (el.dataset.press === 'lift' ? -4 : el.dataset.press === 'flat' ? 0 : -1);

  const up = (): void => {
    const el = pressed;
    if (!el) return;
    pressed = null;
    to(el, { s: 1 }, { r: 0.35, force: true });
  };
  const down = (event: PointerEvent): void => {
    if (event.button > 0) return;
    const el = find(event);
    if (!el) return;
    pressed = el;
    to(el, { s: el.dataset.press === 'lift' ? 0.985 : 0.965 }, { r: 0.12, force: true });
  };
  const over = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse') return;
    const el = find(event);
    if (!el || inside(el, event) || rise(el) === 0) return;
    to(el, { y: rise(el) }, { r: 0.3 });
  };
  const out = (event: PointerEvent): void => {
    const el = find(event);
    if (!el || inside(el, event)) return;
    if (rise(el) !== 0) to(el, { y: 0 }, { r: 0.3 });
    if (pressed === el) up();
  };

  root.addEventListener('pointerdown', down);
  root.addEventListener('pointerover', over);
  root.addEventListener('pointerout', out);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
  return () => {
    root.removeEventListener('pointerdown', down);
    root.removeEventListener('pointerover', over);
    root.removeEventListener('pointerout', out);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
  };
}
